<script lang="ts">
    import {
        VpChevronLeftIcon,
        VpCloseIcon,
        VpPlayIcon,
    } from '@decathlon-experience/icons/svelte';
    import { createDialog } from '@melt-ui/svelte';
    import { fade } from 'svelte/transition';
    import dayjs from 'dayjs';

    import { datesInfos, isModalOpen, modalStep } from '$saga/views/Tour/store';
    import { filterTourDatesIsTravelAloneAuthorized } from '$saga/views/Tour/utils';
    import { TOUR_TYPE_FIT, TOUR_TYPE_GIR } from '$saga/constants/tour';
    import { flyDrawer } from '$saga/utils/customTransitions';
    import SelectTourDateStepForm from '$saga/views/Tour/Aside/form/SelectTourDateStepForm.svelte';
    import SelectPaxStepForm from '$saga/views/Tour/Aside/form/SelectPaxStepForm/SelectPaxStepForm.svelte';
    import UnavailableTour from '$saga/views/Tour/Aside/UnavailableTour.svelte';
    import PriceAside from '$saga/views/Tour/Aside/PriceAside.svelte';
    import type {
        AgeRules,
        FormValues,
        TourConfig,
    } from '$saga/views/Tour/types';
    import Button from '$saga/components/Button.svelte';
    import { selectedPrice } from '$saga/views/Tour/store';

    export let tourConfig: TourConfig;

    const isGir = tourConfig.tourType === TOUR_TYPE_GIR;
    const isFit = tourConfig.tourType === TOUR_TYPE_FIT;
    const ageRules: AgeRules = {
        max: {
            isUnder18Allowed:
                tourConfig.maxAgeAllowed != null &&
                tourConfig.maxAgeAllowed < 18,
        },
        min: {
            isUnder18Allowed:
                tourConfig.minAgeAllowed != null &&
                tourConfig.minAgeAllowed < 18,
        },
    };
    const initialFormState: FormValues = {
        selectedDate: null,
        selectedDate1: null,
        adults: ageRules.max.isUnder18Allowed
            ? 0
            : tourConfig.isTravelAloneAuthorized
              ? 1
              : 2,
        children: 0,
        childrenAges: [],
    };

    // Hack to get the writable store as reactive variable
    // import get the var as readonly
    let formValues = { ...initialFormState };
    let sectionRef: HTMLElement;

    // reactive effect on datesInfos in order to update selectedDate in formValues
    // when user select another date from the VisibleDates component
    $: {
        if ($datesInfos) {
            formValues.selectedDate = $datesInfos.selectedDate;
        }
    }

    const availableDates = filterTourDatesIsTravelAloneAuthorized(
        tourConfig.visibleDates,
        tourConfig.isTravelAloneAuthorized,
    );

    const handleNextStep = (date: string) => {
        // reset
        formValues = { ...initialFormState };

        // then apply selected date
        $modalStep = 2;
        formValues.selectedDate = date;
    };

    const resetForm = () => {
        formValues.selectedDate = null;
        $modalStep = 1;
    };

    // Redirects to ebooking funnel or request funnel
    const handleSubmit = (event: SubmitEvent) => {
        const form = event.target as HTMLFormElement;
        form.action =
            tourConfig.isBookable && tourConfig.isEbooking
                ? tourConfig.bookingFormAction
                : tourConfig.bookingRequestAction;
        form.method =
            tourConfig.isBookable && tourConfig.isEbooking ? 'POST' : 'GET';

        if (tourConfig.isBookable) {
            window.triggerAddToCart(true);
        }

        form.submit();
    };

    const {
        elements: { trigger, title, overlay, content, portalled },
        states: { open },
    } = createDialog({
        open: isModalOpen,
        forceVisible: true,
    });

    let isBookingClosed =
        tourConfig.isClosedSales ||
        (isGir && (!tourConfig.isBookable || availableDates.length === 0));
</script>

{#if isBookingClosed}
    <UnavailableTour
        allTourUrl={tourConfig.allTourUrl}
        dict={tourConfig.dict}
        originalPrice={tourConfig.originalPrice}
        rebateAmount={tourConfig.rebateAmount}
        minPrice={tourConfig.minPrice}
    />
{:else}
    <div
        class="fixed bottom-0 left-0 z-20 flex w-full items-center justify-between border-t border-solid border-border-quiet
    bg-container-commercial-neutral p-m text-content-neutral desktop:sticky
    desktop:right-0 desktop:top-[100px] desktop:block desktop:w-[300px] desktop:min-w-[300px] desktop:flex-col desktop:border-none"
    >
        {#if !isBookingClosed}
            <PriceAside
                originalPrice={tourConfig.originalPrice}
                rebateAmount={tourConfig.rebateAmount}
                minPrice={tourConfig.minPrice}
                dict={tourConfig.dict}
            />
        {/if}

        <div class="w-full desktop:mt-m">
            <button
                class="vp-button vp-button--squared vp-button--primary w-full whitespace-nowrap"
                {...$trigger}
                use:trigger
            >
                {#if tourConfig.isBookable && tourConfig.isEbooking}
                    Réserver
                {:else}
                    Demander un devis
                {/if}
            </button>
        </div>
    </div>
{/if}

{#if $open}
    <section {...$portalled} use:portalled>
        <div
            {...$overlay}
            use:overlay
            class="fixed inset-0 z-40 bg-black/50"
            transition:fade={{ duration: 250 }}
        />
        <section
            {...$content}
            use:content
            class="fixed inset-x-0 bottom-0 z-50 flex h-full max-h-[calc(100%)] w-full flex-col bg-white transition-all duration-500 desktop:inset-x-[unset] desktop:right-0 desktop:top-0 desktop:min-w-[33vw] desktop:max-w-[33vw]"
            transition:flyDrawer
        >
            <section
                class="flex h-full flex-col overflow-hidden"
                id="formContentContainer"
            >
                <!-- Header -->
                <div
                    class="sticky top-0 z-50 mx-m flex min-h-[65px] items-center justify-center border-b border-solid border-border-quiet bg-white py-m desktop:mx-3xl"
                >
                    {#if $modalStep === 2}
                        <button
                            on:click={() => {
                                $modalStep = 1;
                            }}
                        >
                            <VpChevronLeftIcon
                                class="size-xl text-content-neutral"
                            />
                        </button>
                    {/if}
                    <div class="mx-auto" {...$title} use:title>
                        {#if $modalStep === 1}
                            <div class="font-semibold">Date de départ</div>
                        {:else if $modalStep === 2}
                            <div class="flex flex-col gap-2xs">
                                <div class="text-center">
                                    <span class="font-semibold">Dès</span>
                                    <span class="text-[20px] font-semibold">
                                        {#if isGir}
                                            {`${$datesInfos.price}€`}
                                        {:else if isFit}
                                            {$selectedPrice?.actualPrice ??
                                                tourConfig.minPrice}
                                        {/if}
                                    </span>
                                    <span class="text-sm">/personne</span>
                                </div>
                                <div
                                    class="text-center text-sm text-content-quiet"
                                >
                                    départ du {dayjs(
                                        formValues.selectedDate,
                                        'YYYY-MM-DD',
                                    ).format('DD MMM YYYY')} au
                                    {dayjs(
                                        formValues.selectedDate,
                                        'YYYY-MM-DD',
                                    )
                                        .add(tourConfig.tourDuration - 1, 'day')
                                        .format('DD MMM YYYY')}
                                </div>
                            </div>
                        {/if}
                    </div>
                    {#if $modalStep === 1}
                        <button
                            on:click={() => {
                                $open = false;
                                resetForm();
                            }}
                            aria-label="Close"
                        >
                            <VpCloseIcon class="size-xl text-content-neutral" />
                        </button>
                    {/if}
                </div>

                <!-- Content-->
                <form
                    bind:this={sectionRef}
                    id="prefunnel"
                    on:submit|preventDefault|stopPropagation={handleSubmit}
                    class="no-scrollbar flex flex-1 flex-col overflow-y-auto px-l desktop:px-3xl"
                >
                    <input
                        class="hidden"
                        name="startDate"
                        value={formValues.selectedDate
                            ? formValues.selectedDate
                            : ''}
                    />
                    <input
                        class="hidden"
                        name="token"
                        bind:value={tourConfig.token}
                    />
                    <input
                        class="hidden"
                        name="code"
                        bind:value={tourConfig.code}
                    />
                    <input
                        class="hidden"
                        name="csrf_token"
                        bind:value={tourConfig.csrfToken}
                    />

                    {#if $modalStep === 1}
                        <SelectTourDateStepForm
                            tourType={tourConfig.tourType}
                            dates={availableDates}
                            closedSales={tourConfig.closedSales}
                            minPricePerDays={tourConfig.minPricePerDays}
                            {handleNextStep}
                            overflowContainer={sectionRef}
                        />
                    {:else if $modalStep === 2}
                        <SelectPaxStepForm
                            tourType={tourConfig.tourType}
                            bookingType={tourConfig.bookingType}
                            isTravelAloneAuthorized={tourConfig.isTravelAloneAuthorized}
                            minAgeAllowed={tourConfig.minAgeAllowed}
                            maxAgeAllowed={tourConfig.maxAgeAllowed}
                            childrenAges={tourConfig.childrenAges}
                            {ageRules}
                            bind:formValues
                        />
                    {/if}
                </form>
                <!-- button is here for sticky layout hierarchy needs -->
                {#if $modalStep === 2}
                    <div
                        class="sticky bottom-0 left-0 flex w-full justify-between border-t border-solid border-border-quiet bg-white px-l py-s desktop:px-3xl"
                    >
                        <Button
                            class="w-full"
                            type="submit"
                            form="prefunnel"
                            endIcon={VpPlayIcon}>Continuer</Button
                        >
                    </div>
                {/if}
            </section>
        </section>
    </section>
{/if}
